import { type Optional } from "matrix-events-sdk";

import { _t, getUserLanguage } from "./languageHandler";
import { getUserTimezone } from "./TimezoneHandler";
import { toCamelCase } from "./dth/utils";
import SettingsStore from "./settings/SettingsStore";

export const MINUTE_MS = 60000;
export const HOUR_MS = MINUTE_MS * 60;
export const DAY_MS = HOUR_MS * 24;

/**
 * Formats a number of seconds into a human-readable string.
 * @param inSeconds
 */
export function formatSeconds(inSeconds: number): string {
    const isNegative = inSeconds < 0;
    inSeconds = Math.abs(inSeconds);

    const hours = Math.floor(inSeconds / (60 * 60))
        .toFixed(0)
        .padStart(2, "0");
    const minutes = Math.floor((inSeconds % (60 * 60)) / 60)
        .toFixed(0)
        .padStart(2, "0");
    const seconds = Math.floor((inSeconds % (60 * 60)) % 60)
        .toFixed(0)
        .padStart(2, "0");

    let output = "";
    if (hours !== "00") output += `${hours}:`;
    output += `${minutes}:${seconds}`;

    if (isNegative) {
        output = "-" + output;
    }

    return output;
}

/**
 * Returns array of 7 weekday names, from Sunday to Saturday, internationalised to the user's language.
 * @param weekday - format desired "short" | "long" | "narrow"
 */
export function getDaysArray(weekday: Intl.DateTimeFormatOptions["weekday"] = "short"): string[] {
    const sunday = 1672574400000; // 2023-01-01 12:00 UTC
    const { format } = new Intl.DateTimeFormat(getUserLanguage(), { weekday, timeZone: "UTC" });
    return [...Array(7).keys()].map((day) => format(sunday + day * DAY_MS));
}

/**
 * Returns array of 12 month names, from January to December, internationalised to the user's language.
 * @param month - format desired "numeric" | "2-digit" | "long" | "short" | "narrow"
 */
export function getMonthsArray(month: Intl.DateTimeFormatOptions["month"] = "short"): string[] {
    const { format } = new Intl.DateTimeFormat(getUserLanguage(), { month, timeZone: "UTC" });
    return [...Array(12).keys()].map((m) => format(Date.UTC(2021, m)));
}

// XXX: Ideally we could just specify `hour12: boolean` but it has issues on Chrome in the `en` locale
// https://support.google.com/chrome/thread/29828561?hl=en
export function getTwelveHourOptions(showTwelveHour: boolean): Intl.DateTimeFormatOptions {
    return {
        hourCycle: showTwelveHour ? "h12" : "h23",
    };
}

/**
 * Formats a given date to a date & time string.
 *
 * The output format depends on how far away the given date is from now.
 * Will use the browser's default time zone.
 * If the date is today it will return a time string excluding seconds. See {@formatTime}.
 * If the date is within the last 6 days it will return the name of the weekday along with the time string excluding seconds.
 * If the date is within the same year then it will return the weekday, month and day of the month along with the time string excluding seconds.
 * Otherwise, it will return a string representing the full date & time in a human friendly manner. See {@formatFullDate}.
 * @param date - date object to format
 * @param showTwelveHour - whether to use 12-hour rather than 24-hour time. Defaults to `false` (24 hour mode).
 *        Overrides the default from the locale, whether `true` or `false`.
 * @param locale - the locale string to use, in BCP 47 format, defaulting to user's selected application locale
 */
export function formatDate(date: Date, showTwelveHour?: boolean, locale?: string): string {
    return formatHumanDateTime(date, { showTwelveHour, locale, useLongName: true });
    //OLD
    // const _locale = locale ?? getUserLanguage();
    // showTwelveHour = showTwelveHour ?? getShowTwelveHourTimeOrDefault();
    // const now = new Date();
    // if (date.toDateString() === now.toDateString()) {
    //     return formatTime(date, showTwelveHour, _locale);
    // } else if (now.getTime() - date.getTime() < 6 * DAY_MS) {
    //     // Time is within the last 6 days (or in the future)
    //     return new Intl.DateTimeFormat(_locale, {
    //         ...getTwelveHourOptions(showTwelveHour),
    //         weekday: "short",
    //         hour: "numeric",
    //         minute: "2-digit",
    //         timeZone: getUserTimezone(),
    //     }).format(date);
    // } else if (now.getFullYear() === date.getFullYear()) {
    //     return new Intl.DateTimeFormat(_locale, {
    //         ...getTwelveHourOptions(showTwelveHour),
    //         weekday: "short",
    //         month: "short",
    //         day: "numeric",
    //         hour: "numeric",
    //         minute: "2-digit",
    //         timeZone: getUserTimezone(),
    //     }).format(date);
    // }
    // return formatFullDate(date, showTwelveHour, false, _locale);
}

/**
 * Formats a given date to a date & time string, optionally including seconds.
 * Will use the browser's default time zone.
 * @example "Thursday, 17 November 2022 at 16:58" in en-GB locale with showTwelveHour=false and showSeconds=false
 * @example "Thu, 17 Nov 2022, 4:58:32 pm" in en-GB locale with showTwelveHour=true and showSeconds=true
 * @param date - date object to format
 * @param showTwelveHour - whether to use 12-hour rather than 24-hour time. Defaults to `false` (24 hour mode).
 *        Overrides the default from the locale, whether `true` or `false`.
 * @param showSeconds - whether to include seconds in the time portion of the string
 * @param locale - the locale string to use, in BCP 47 format, defaulting to user's selected application locale
 */
export function formatFullDate(date: Date, showTwelveHour?: boolean, showSeconds = true, locale?: string): string {
    showTwelveHour = showTwelveHour ?? getShowTwelveHourTimeOrDefault();
    return formatHumanDateTime(date, {
        showTwelveHour,
        showSeconds,
        forceFullDate: true,
        locale,
    });
    //OLD
    // return new Intl.DateTimeFormat(locale ?? getUserLanguage(), {
    //     ...getTwelveHourOptions(showTwelveHour),
    //     weekday: "short",
    //     month: "short",
    //     day: "numeric",
    //     year: "numeric",
    //     hour: "numeric",
    //     minute: "2-digit",
    //     second: showSeconds ? "2-digit" : undefined,
    //     timeZone: getUserTimezone(),
    // }).format(date);
}

/**
 * Formats dates to be compatible with attributes of a `<input type="date">`. Dates
 * should be formatted like "2020-06-23" (formatted according to ISO8601).
 *
 * @param date The date to format.
 * @returns The date string in ISO8601 format ready to be used with an `<input>`
 */
export function formatDateForInput(date: Date): string {
    const year = `${date.getFullYear()}`.padStart(4, "0");
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Formats a given date to a time string including seconds.
 * Will use the browser's default time zone.
 * @example "4:58:32 PM" in en-GB locale with showTwelveHour=true
 * @example "16:58:32" in en-GB locale with showTwelveHour=false
 * @param date - date object to format
 * @param showTwelveHour - whether to use 12-hour rather than 24-hour time. Defaults to `false` (24 hour mode).
 *        Overrides the default from the locale, whether `true` or `false`.
 * @param locale - the locale string to use, in BCP 47 format, defaulting to user's selected application locale
 */
export function formatFullTime(date: Date, showTwelveHour?: boolean, locale?: string): string {
    return formatHumanTime(date, { showTwelveHour, showSeconds: true, locale });
    //OLD
    // showTwelveHour = showTwelveHour ?? getShowTwelveHourTimeOrDefault();
    // return new Intl.DateTimeFormat(locale ?? getUserLanguage(), {
    //     ...getTwelveHourOptions(showTwelveHour),
    //     hour: "numeric",
    //     minute: "2-digit",
    //     second: "2-digit",
    //     timeZone: getUserTimezone(),
    // }).format(date);
}

/**
 * Formats a given date to a time string excluding seconds.
 * Will use the browser's default time zone.
 * @example "4:58 PM" in en-GB locale with showTwelveHour=true
 * @example "16:58" in en-GB locale with showTwelveHour=false
 * @param date - date object to format
 * @param showTwelveHour - whether to use 12-hour rather than 24-hour time. Defaults to `false` (24 hour mode).
 *        Overrides the default from the locale, whether `true` or `false`.
 * @param locale - the locale string to use, in BCP 47 format, defaulting to user's selected application locale
 */
export function formatTime(date: Date, showTwelveHour?: boolean, locale?: string): string {
    return formatHumanTime(date, { showTwelveHour, showSeconds: false, locale });
    //OLD
    // showTwelveHour = showTwelveHour ?? getShowTwelveHourTimeOrDefault();
    // return new Intl.DateTimeFormat(locale ?? getUserLanguage(), {
    //     ...getTwelveHourOptions(showTwelveHour),
    //     hour: "numeric",
    //     minute: "2-digit",
    //     timeZone: getUserTimezone(),
    // }).format(date);
}

export function formatTimeLeft(inSeconds: number): string {
    const hours = Math.floor(inSeconds / (60 * 60)).toFixed(0);
    const minutes = Math.floor((inSeconds % (60 * 60)) / 60).toFixed(0);
    const seconds = Math.floor((inSeconds % (60 * 60)) % 60).toFixed(0);

    if (hours !== "0") {
        return _t("time.hours_minutes_seconds_left", {
            hours,
            minutes,
            seconds,
        });
    }

    if (minutes !== "0") {
        return _t("time.minutes_seconds_left", {
            minutes,
            seconds,
        });
    }

    return _t("time.seconds_left", {
        seconds,
    });
}

function withinPast24Hours(prevDate: Date, nextDate: Date): boolean {
    return Math.abs(prevDate.getTime() - nextDate.getTime()) <= DAY_MS;
}

function withinCurrentDay(prevDate: Date, nextDate: Date): boolean {
    return withinPast24Hours(prevDate, nextDate) && prevDate.getDay() === nextDate.getDay();
}

function withinCurrentYear(prevDate: Date, nextDate: Date): boolean {
    return prevDate.getFullYear() === nextDate.getFullYear();
}

export function wantsDateSeparator(prevEventDate: Optional<Date>, nextEventDate: Optional<Date>): boolean {
    if (!nextEventDate || !prevEventDate) {
        return false;
    }
    // Return early for events that are > 24h apart
    if (!withinPast24Hours(prevEventDate, nextEventDate)) {
        return true;
    }

    // Compare weekdays
    return prevEventDate.getDay() !== nextEventDate.getDay();
}

export function formatFullDateNoDay(date: Date): string {
    const locale = getUserLanguage();
    return _t("time.date_at_time", {
        date: date.toLocaleDateString(locale).replace(/\//g, "-"),
        time: date.toLocaleTimeString(locale).replace(/:/g, "-"),
    });
}

/**
 * Returns an ISO date string without textual description of the date (ie: no "Wednesday" or similar)
 * @param date The date to format.
 * @returns The date string in ISO format.
 */
export function formatFullDateNoDayISO(date: Date): string {
    return date.toISOString();
}

/**
 * Formats a given date to a string.
 * Will use the browser's default time zone.
 * @example 17/11/2022 in en-GB locale
 * @param date - date object to format
 * @param locale - the locale string to use, in BCP 47 format, defaulting to user's selected application locale
 */
export function formatFullDateNoDayNoTime(date: Date, locale?: string): string {
    return new Intl.DateTimeFormat(locale ?? getUserLanguage(), {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        timeZone: getUserTimezone(),
    }).format(date);
}

export function formatRelativeTime(date: Date, showTwelveHour?: boolean): string {
    showTwelveHour = showTwelveHour ?? getShowTwelveHourTimeOrDefault();
    const now = new Date();
    if (withinCurrentDay(date, now)) {
        return formatTime(date, showTwelveHour);
    } else {
        const months = getMonthsArray();
        let relativeDate = `${months[date.getMonth()]} ${date.getDate()}`;

        if (!withinCurrentYear(date, now)) {
            relativeDate += `, ${date.getFullYear()}`;
        }
        return relativeDate;
    }
}

/**
 * Formats duration in ms to human-readable string
 * Returns value in the biggest possible unit (day, hour, min, second)
 * Rounds values up until unit threshold
 * i.e. 23:13:57 -> 23h, 24:13:57 -> 1d, 44:56:56 -> 2d
 */
export function formatDuration(durationMs: number): string {
    if (durationMs >= DAY_MS) {
        return _t("time.short_days", { value: Math.round(durationMs / DAY_MS) });
    }
    if (durationMs >= HOUR_MS) {
        return _t("time.short_hours", { value: Math.round(durationMs / HOUR_MS) });
    }
    if (durationMs >= MINUTE_MS) {
        return _t("time.short_minutes", { value: Math.round(durationMs / MINUTE_MS) });
    }
    return _t("time.short_seconds", { value: Math.round(durationMs / 1000) });
}

/**
 * Formats duration in ms to human-readable string
 * Returns precise value down to the nearest second
 * i.e. 23:13:57 -> 23h 13m 57s, 44:56:56 -> 1d 20h 56m 56s
 */
export function formatPreciseDuration(durationMs: number): string {
    const days = Math.floor(durationMs / DAY_MS);
    const hours = Math.floor((durationMs % DAY_MS) / HOUR_MS);
    const minutes = Math.floor((durationMs % HOUR_MS) / MINUTE_MS);
    const seconds = Math.floor((durationMs % MINUTE_MS) / 1000);

    if (days > 0) {
        return _t("time.short_days_hours_minutes_seconds", { days, hours, minutes, seconds });
    }
    if (hours > 0) {
        return _t("time.short_hours_minutes_seconds", { hours, minutes, seconds });
    }
    if (minutes > 0) {
        return _t("time.short_minutes_seconds", { minutes, seconds });
    }
    return _t("time.short_seconds", { value: seconds });
}

/**
 * Formats a timestamp to a short date
 * Similar to {@formatFullDateNoDayNoTime} but with 2-digit on day, month, year.
 * @example 25/12/22 in en-GB locale
 * @param timestamp - epoch timestamp
 * @param locale - the locale string to use, in BCP 47 format, defaulting to user's selected application locale
 * @returns {string} formattedDate
 */
export const formatLocalDateShort = (timestamp: number, locale?: string): string =>
    new Intl.DateTimeFormat(locale ?? getUserLanguage(), {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        timeZone: getUserTimezone(),
    }).format(timestamp);


//#region PAS CUSTOM DATE UTILS

/**
 * Returns whether the user has enabled 12-hour time in their settings, or false if not set.
 * @returns boolean
 */
export function getShowTwelveHourTimeOrDefault(): boolean {
    return SettingsStore.getValue("showTwelveHourTimestamps") ?? false;
}

/**
 * Formats a date into a human-friendly, localized date-only string.
 *
 * Rules:
 * - Today      -> localized "today"
 * - Yesterday  -> localized "yesterday"
 * - Last week  -> weekday
 * - Last year  -> day + month
 * - Older      -> day + month + year
 * - Full Date (forceFullDate: true) -> weekday + day + month + year
 * 
 * es. CASE                           → LONG NAME                   → SHORT NAME        → ITALIAN LONG NAME          → ITALIAN SHORT NAME   
 *     FULL DATE (forceFullDate: true)→ wednesday 23 february 2022  → wed 23 feb 2022   → mercoledì 23 febbraio 2022 → mer 23 feb 2022
 *     TODAY                          → today                       → today             → oggi                       → oggi
 *     YESTERDAY                      → yesterday                   → yesterday         → ieri                       → ieri
 *     3 DAYS AGO (last week)         → monday                      → mon               → lunedì                     → lun
 *     6 DAYS AGO (last week)         → friday                      → fri               → venerdì                    → ven
 *     10 DAYS AGO (same year)        → january 23                  → jan 23            → 23 gennaio                 → 23 gen
 *     3 MONTHS AGO (same year)       → november 3                  → nov 3             → 3 novembre                 → 3 nov
 *     LAST YEAR                      → february 3, 2025            → feb 3, 2025       → 3 febbraio 2025            → 3 feb 2025
 *     5 YEARS AGO                    → march 10, 2021              → mar 10, 2021      → 10 marzo 2021              → 10 mar 2021
 *
 * @param date - date object to format
 * @param options - formatting options
 *        options.locale?: string - BCP 47 locale string, defaults to user's language
 *        options.useLongName?: boolean - whether to use long version of month and weekday names, defaults to true
 *        options.camelCaseName?: boolean - whether to use camel case for the returned string, defaults to true
 *        options.forceFullDate?: boolean - whether to force the full date format regardless of how old the date is, defaults to false
 * @returns formatted date string 
*/
export function formatHumanDateOnly(
    date: Date,
    options: {
        locale?: string,
        useLongName?: boolean,
        camelCaseName?: boolean,
        forceFullDate?: boolean,
    } = {}
): string {
    const { locale, useLongName = true, camelCaseName = true, forceFullDate = false } = options;
    const _locale = locale ?? getUserLanguage();
    const timeZone = getUserTimezone();
    let returnString;

    const now = new Date();

    // Normalize to midnight (timezone-safe)
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const startOfDate = new Date(date);
    startOfDate.setHours(0, 0, 0, 0);

    const diffDays =
        Math.round(
            (startOfToday.getTime() - startOfDate.getTime()) / DAY_MS
        );

    // FULL DATE
    if (forceFullDate) {
        returnString = new Intl.DateTimeFormat(_locale, {
            weekday: useLongName ? "long" : "short",
            day: "numeric",
            month: useLongName ? "long" : "short",
            year: "numeric",
            timeZone,
        }).format(date);
    }
    // TODAY
    else if (diffDays === 0) {
        returnString = new Intl.RelativeTimeFormat(_locale, { numeric: "auto" })
            .format(0, "day");
    }
    // YESTERDAY
    else if (diffDays === 1) {
        returnString = new Intl.RelativeTimeFormat(_locale, { numeric: "auto" })
            .format(-1, "day");
    }
    // LAST WEEK (within 7 days)
    else if (diffDays < 7) {
        returnString = new Intl.DateTimeFormat(_locale, {
            weekday: useLongName ? "long" : "short",
            timeZone,
        }).format(date);
    }
    // LAST YEAR (same year)
    else if (now.getFullYear() === date.getFullYear()) {
        returnString = new Intl.DateTimeFormat(_locale, {
            day: "numeric",
            month: useLongName ? "long" : "short",
            timeZone,
        }).format(date);
    } else {
        // OLDER
        returnString = new Intl.DateTimeFormat(_locale, {
            day: "numeric",
            month: useLongName ? "long" : "short",
            year: "numeric",
            timeZone,
        }).format(date);
    }
    return camelCaseName ? toCamelCase(returnString) : returnString;

}

/**
 * Formats a given date to a time string including seconds.
 * Will use the browser's default time zone.
 * @example "4:58:32 PM" in en-GB locale with showTwelveHour=true
 * @example "16:58:32" in en-GB locale with showTwelveHour=false
 * @param date - date object to format
 * @param options - formatting options
 *        options.locale?: string - BCP 47 locale string, defaults to user's language
 *        options.showTwelveHour?: boolean - whether to use 12-hour rather than 24-hour time. Defaults to `false` (24 hour mode).
 *        options.showSeconds?: boolean - whether to include seconds in the time portion of the string, defaults to false
 *        options.timeZone?: string - the time zone to use, defaults to the user's time zone or browser default if not set
*/
export function formatHumanTime(
    date: Date,
    options: { locale?: string; showTwelveHour?: boolean, showSeconds?: boolean, timeZone?: string } = {}
): string {
    const { locale, showTwelveHour = getShowTwelveHourTimeOrDefault(), showSeconds = false, timeZone = getUserTimezone() } = options;
    return new Intl.DateTimeFormat(locale ?? getUserLanguage(), {
        ...getTwelveHourOptions(showTwelveHour),
        hour: "numeric",
        minute: "2-digit",
        second: showSeconds ? "2-digit" : undefined,
        timeZone,
    }).format(date);
}

/**
 * Formats a given date to a human-friendly string with long weekday and month names, including time.
 * Will use the browser's default time zone.
 * @param date - date object to format
 * @param options - formatting options
 *        options.locale?: string - BCP 47 locale string, defaults to user's language
 *        options.useLongName?: boolean - whether to use long version of month and weekday names, defaults to true
 *        options.camelCaseName?: boolean - whether to use camel case for the returned string, defaults to true
 *        options.showTwelveHour?: boolean - whether to use 12-hour rather than 24-hour time. Defaults to `false` (24 hour mode).
 *        options.showSeconds?: boolean - whether to include seconds in the time portion of the string, defaults to false
 *        options.forceFullDate?: boolean - whether to force the full date format, defaults to false
 *        options.timeZone?: string - the time zone to use, defaults to the user's time zone or browser default if not set
 * @returns formatted date and time string
 */
export function formatHumanDateTime(
    date: Date,
    options: {
        locale?: string,
        // DATE OPTIONS
        useLongName?: boolean,
        camelCaseName?: boolean,
        forceFullDate?: boolean,
        // TIME OPTIONS
        showTwelveHour?: boolean,
        showSeconds?: boolean,
        timeZone?: string,
    } = {}
) {
    return `${formatHumanDateOnly(date, options)} ${formatHumanTime(date, options)}`;
}

/**
 * Formats a given date to a day date string.
 *
 * The output format depends on how far away the given date is from now.
 * Will use the browser's default time zone.
 * it will return a string representing the full date in a human friendly manner.
 * @example "17/11/2022" in en-GB locale
 * @param date - date object to format
 * @param locale - the locale string to use, in BCP 47 format, defaulting to user's selected application locale
 */
export function formatDayDate(date: Date, locale?: string): string {
    const _locale = locale ?? getUserLanguage();
    return new Intl.DateTimeFormat(_locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: getUserTimezone(),
    }).format(date);
}
//#endregion